import React, { useState, useEffect, useContext, useMemo } from 'react';
import { AppContext } from '../../context/index';
import {
    Modal,
    ModalHeader,
    Row,
    Col,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
    ModalBody,
    ModalFooter
} from 'reactstrap';
import ReactTooltip from 'react-tooltip';

import employeeMapping from './employeeMapping';
import * as Yup from 'yup';
import moment from 'moment';
import { restrictedEmails } from '../adminEmails';
import { asyncHandler, formatDatetime, getTsDate, getUnitsMap, notify } from '../../utils';
import _ from 'lodash';
import classnames from 'classnames';

import ModalManageAirlines from './ModalManageAirlines';
import ModalManageIp from './ModalManageIp';
import ModalAccessMap from './ModalAccessMap';
import styled from 'styled-components';
import ModalNativeAccessMap from './ModalNativeAccessMap';
import { Field, Formik } from 'formik';
import SaveButton from '../custom/SaveButton';
import Files from './Files';
import apiClient from '../../apiClient';
import EmployeeForm from './EmployeeForm';
import EmployeeLog from './EmployeeLog';
import ActionIcon from '../custom/ActionIcon';
import ModalUpdatePhoto from './ModalUpdatePhoto';
import AdditionalEmployeeData from './AdditionalEmployeeData';
import { Button } from 'reactstrap';
import Icon from '../custom/Icon';
import useLoading from '../../customHooks/useLoading';

export default function ModalManageEmployee({
    user,
    units,
    modal,
    setModal,
    newEmployee,
    selectedEmployee,
    setSelectedEmployee,
    accessLevels,
    handleAddEditEmployee,
    getAirlines,
    airlines,
    existingEmployeeData,
    nativeAccessMapSchema,
    deleteEmployee,
    jobTitles
}) {

    const { setLoading } = useLoading();
    const toggle = () => setModal(!modal);

    const unitsMap = useMemo(() => {
        return getUnitsMap(units);
    }, [units]);

    const { wiki } = useContext(AppContext);
    const { setCustomWikiTitle } = wiki;
    const statusOptions = ['ACTIVE', 'SUSPENDED', 'FURLOUGHED', 'TERMINATED', 'REVOKED'];

    const [initialValues, setInitialValues] = useState({});
    const [sortedAccessLevels, setSortedAccessLevels] = useState([]);
    const [restrictedAccess, setRestrictedAccess] = useState(false);
    const [selectedUnits, setSelectedUnits] = useState([]);

    // Manage Airlines
    const [modalAirlines, setModalAirlines] = useState(false);
    const [selectedAirlines, setSelectedAirlines] = useState({});

    // Access Map
    const [manageAccessMap, setManageAccessMap] = useState(false);

    // Native Access Map
    const [manageNativeAccessMap, setManageNativeAccessMap] = useState(false);

    const [modalUpdatePhoto, setModalUpdatePhoto] = useState(false);

    const [validGraphUser, setValidGraphUser] = useState(false);

    const { employeeNumbers, employeeEmails } = existingEmployeeData;
    const [triggerAdpSync, setTriggerAdpSync] = useState(0);

    const uniqueEmail = (s_email) => {
        if (newEmployee && employeeEmails.has(s_email)) {
            return false;
        }
        return true;
    };

    const uniqueNumber = (i_employee_number) => {
        if (newEmployee && employeeNumbers.has(i_employee_number)) {
            return false;
        }
        return true;
    };

    const handleModalAirlines = async () => {
        if (airlines.length === 0) {
            await getAirlines();
        }
        setModalAirlines(true);
    };

    const addRemoveAirline = (code) => {
        setSelectedAirlines((prevState) => {
            const copy = Object.assign({}, prevState);
            if (!copy[code]) {
                copy[code] = true;
            } else {
                delete copy[code];
            }
            return copy;
        });
    };

    const resolveSelectedUnits = (selectedEmployee) => {
        const selectedUnits = [];
        const employeeUnits = selectedEmployee.s_unit.split(',');
        for (let i = 0; i < employeeUnits.length; i++) {
            selectedUnits.push(employeeUnits[i]);
        }
        return selectedUnits;
    };

    const resolveSelectedAirlines = (selectedEmployee) => {
        let useString = selectedEmployee.s_airline_codes;
        if (useString[useString.length - 1] === ',') {
            useString = useString.substr(0, useString.length - 1);
        }

        const airlines = useString.split(',');
        const map = {};
        airlines.map((airline) => (map[airline] = true));

        return map;
    };

    useEffect(() => {
        if (selectedEmployee.s_unit) {
            setSelectedUnits(resolveSelectedUnits(selectedEmployee));
        }
        if (selectedEmployee.s_airline_codes) {
            setSelectedAirlines(resolveSelectedAirlines(selectedEmployee));
        }
        if (newEmployee) {
            setSelectedUnits([user.s_unit]);
        }
    }, [newEmployee, selectedEmployee]);

    useEffect(() => {
        setInitialValues(resolveValues(employeeMapping));
        setSortedAccessLevels(
            accessLevels.sort((a, b) => a.i_access_level - b.i_access_level)
        );
    }, [modal, selectedEmployee, accessLevels]);

    useEffect(() => {
        if (user && user.s_email) {
            if (restrictedEmails.includes(user.s_email.toLowerCase())) {
                setRestrictedAccess(true);
            } else {
                setRestrictedAccess(false);
            }
        }
    }, [user]);

    const resolveValues = (mapping) => {
        const values = {};
        const dates = [
            'd_hire',
            'd_terminated',
            'd_suspended_start',
            'd_suspended_end',
            'd_furlough_start',
            'd_furlough_end',
        ];
        for (let i = 0; i < mapping.length; i++) {
            const key = mapping[i];
            if (newEmployee) {
                if (key === 's_status') {
                    values[key] = statusOptions[0];
                } else if (key === 's_unit') {
                    values[key] = units[0];
                } else if (key === 'i_access_level') {
                    values[key] = 1;
                } else if (key === 'b_internal') {
                    values[key] = false;
                } else {
                    values[key] = '';
                }
                values['s_home_station'] = user.s_unit;
                values['s_unit'] = user.s_unit;
            } else {
                const { OEmployeeCensus, s_status } = selectedEmployee;

                if (dates.indexOf(key) !== -1) {
                    if (selectedEmployee && selectedEmployee.OEmployeeCensus) {
                        for (let i = OEmployeeCensus.length - 1; i > -1; i--) {
                            const currentRecord = OEmployeeCensus[i];
                            if (
                                currentRecord?.s_type?.toUpperCase() ===
                                s_status.toUpperCase()
                            ) {
                                if (currentRecord[key]) {
                                    // values[key] = moment(currentRecord[key]).local().format('YYYY-MM-DD');
                                    const utcDate = moment.utc(
                                        currentRecord[key]
                                    );
                                    const localDate = utcDate.local();
                                    values[key] = currentRecord[key];
                                }
                            }
                        }
                    }
                    const utcDate = moment.utc(
                        selectedEmployee && selectedEmployee[key]
                    ).format('YYYY-MM-DD');
                    if (selectedEmployee[key]) {
                        values[key] = utcDate;
                    }
                } else {
                    values[key] = selectedEmployee && selectedEmployee[key];
                    values['s_status'] = s_status;
                    values['s_phone_num'] = _.get(selectedEmployee, `s_phone_num.length`, 0) >= 10 ? selectedEmployee.s_phone_num.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3') : '';
                    values['s_middle_name'] = selectedEmployee['s_middle_name'] || '';
                }
            }
        }
        return values;
    };

    const EmployeeSchema = Yup.object().shape({
        s_email: Yup.string().email('Invalid email').required('Required'),
        s_unit: Yup.string().required('Required'),
        s_home_station: Yup.string().required('Required'),
        i_employee_number: Yup.number().required('Required'),
        s_first_name: Yup.string().required('Required'),
        s_last_name: Yup.string().required('Required'),
        s_phone_num: Yup.string()
            .required("Phone number is required")
            .test('len', 'Phone number must be 10 digits', val => {
                const digits = val ? val.replace(/\D/g, '') : '';
                return digits.length === 10;
            }),
        // s_job_title: Yup.string().required('Required'),
        s_department: Yup.string().required('Required'),
        s_status: Yup.string().required('Required'),
        s_work_type: Yup.string().notRequired(),
        s_service_type: Yup.string().notRequired(),
        i_job_id: Yup.number().required(),
        d_terminated: Yup.date().when('s_status', {
            is: 'TERMINATED',
            then: Yup.date().required('Required'),
        }),
        d_suspended_start: Yup.date().when('s_status', {
            is: 'SUSPENDED',
            then: Yup.date().required('Required'),
        }),
        d_suspended_end: Yup.date().when('s_status', {
            is: 'SUSPENDED',
            then: Yup.date().required('Required'),
        }),
        d_furlough_start: Yup.date().when('s_status', {
            is: 'FURLOUGHED',
            then: Yup.date().required('Required'),
        }),
        d_furlough_end: Yup.date().when('s_status', {
            is: 'FURLOUGHED',
            then: Yup.date().required('Required'),
        })
    });

    const handleSelectUnit = (unit, setFieldValue) => {
        let units = _.cloneDeep(selectedUnits);
        if (units.includes(unit)) {
            units = units.filter((u) => u !== unit);
        } else {
            units.push(unit);
        }
        setSelectedUnits(units);
        setFieldValue('s_unit', units.toString());
    };

    // IP Addresses:

    const ipAddresses = useMemo(() => {
        if (selectedEmployee && selectedEmployee.s_ip_subnet) {
            const map = {};
            const array = selectedEmployee.s_ip_subnet.split(',');
            for (let i = 0; i < array.length; i++) {
                map[i] = array[i];
            }
            return map;
        }
        return {};
    }, [selectedEmployee]);

    const [ipAddress, setIpAddress] = useState('');
    const [createNewIp, setCreateNewIp] = useState(false);
    const [updateIndex, setUpdateIndex] = useState('');
    const [modalIpAddress, setModalIpAddress] = useState(false);

    const handleCreateUpdateIp = (create, address, updateIndex = '') => {
        setCreateNewIp(create);
        setIpAddress(address);
        setModalIpAddress(true);
        setUpdateIndex(updateIndex);
    };

    const saveIp = asyncHandler(async (deleteIp) => {
        const ipAddressesCopy = _.cloneDeep(ipAddresses);

        if (!createNewIp) {
            if (deleteIp) {
                delete ipAddressesCopy[updateIndex];
            } else {
                ipAddressesCopy[updateIndex] = ipAddress;
            }
        } else {
            const numKeys = Object.keys(ipAddresses).length; // This will be the next index to use.
            ipAddressesCopy[numKeys] = ipAddress;
        }

        const array = [];
        for (let key in ipAddressesCopy) {
            array.push(ipAddressesCopy[key]);
        }

        const string = array.join(',');

        // Update employee ipAddresses
        await apiClient.post('/updateEmployeeIpAddresses', {
            s_ip_subnet: string,
            id: selectedEmployee.id,
            employeeLog: `IP Address Updated: ${selectedEmployee.s_ip_subnet} -> ${string}`,
            logCreated: getTsDate(),
            logCreatedBy: user.s_email,
        });

        // Update selectedEmployee object with new ipAddress
        const selectedEmployeeCopy = _.cloneDeep(selectedEmployee);
        selectedEmployeeCopy.s_ip_subnet = string;
        setSelectedEmployee(selectedEmployeeCopy);

        setModalIpAddress(false);
    });

    const [activeTab, setActiveTab] = useState('INFO');
    const toggleTab = (tab) => {
        if (activeTab !== tab) {
            setActiveTab(tab);
        }
    };

    const [employeeFiles, setEmployeeFiles] = useState([]);

    useEffect(() => {
        setEmployeeFiles([]);
        const getEmployeeFiles = async () => {
            try {
                const res = await apiClient.get(`/employeeFiles?employee_id=${selectedEmployee.id}&email=${selectedEmployee.s_email}`);
                const { files, validGraphUser } = res.data;
                setEmployeeFiles(files);
                setValidGraphUser(validGraphUser);
            } catch (err) {
                alert(err);
            }
        }
        if (selectedEmployee && selectedEmployee.id) {
            getEmployeeFiles();
        }
    }, [selectedEmployee]);

    const syncWithAdp = async () => {
        try {
            setLoading(true);
            const { s_job_title, s_department, s_status, s_work_type, s_service_type, s_phone_num } = selectedEmployee;
            const res = await apiClient.put(`/sync-with-adp`, {
                s_adp_id: selectedEmployee.s_adp_id,
                existing: {
                    s_job_title,
                    s_department,
                    s_status,
                    s_work_type,
                    s_service_type,
                    s_phone_num
                }
            });
            if (res.data.success) {
                const {
                    s_status,
                    t_modified,
                    s_modified_by,
                    s_job_title,
                    s_department,
                    s_service_type,
                    i_job_id,
                    s_work_type,
                    s_phone_num
                } = res.data.data;
                setSelectedEmployee(prev => {
                    const copy = _.cloneDeep(prev);
                    copy.s_job_title = s_job_title;
                    copy.s_department = s_department;
                    copy.s_service_type = s_service_type;
                    copy.s_status = s_status;
                    copy.s_work_type = s_work_type;
                    copy.t_modified = t_modified;
                    copy.s_modified_by = s_modified_by;
                    copy.i_job_id = i_job_id;
                    copy.s_phone_num = s_phone_num;
                    return copy;
                });
                setTriggerAdpSync(prev => prev + 1);
                notify('Employee Synced Successfully');
            }
        } catch (err) {
            notify('Error, please try again', 'error');
        } finally {
            setLoading(false);
        }

    }

    return (
        <div>
            <ReactTooltip />
            <Modal
                isOpen={modal}
                toggle={toggle}
                style={{ maxWidth: '1600px', width: '100%' }}
            >
                <ModalHeader>
                    <Row>
                        <Col md={12}>
                            <span className={'float-left'}>
                                {newEmployee ? 'Add' : 'Edit'} Employee
                                {
                                    !newEmployee && (
                                        <FlexContainer>
                                            <ImgUser src={selectedEmployee?.s_photo_link || '/assets/img/user.png'} />
                                            <EditIcon className={'fas fa-edit'} onClick={() => setModalUpdatePhoto(true)} />
                                            <div>
                                                <h6>{selectedEmployee.s_first_name} {selectedEmployee.s_last_name}</h6>
                                                <h6>{selectedEmployee.s_job_title}</h6>
                                                <h6>{selectedEmployee.i_employee_number}</h6>
                                                <h6>{selectedEmployee.s_email}</h6>
                                            </div>
                                            <div>
                                                <h6>Reports to: { }</h6>
                                                <h6>Date of hire: {formatDatetime(selectedEmployee.d_hire, true)}</h6>
                                                <h6>Department: {selectedEmployee.s_department}</h6>
                                            </div>
                                            <div>
                                                {
                                                    validGraphUser ?
                                                        <ShieldIcon className="fa-light fa-id-card text-success" data-tip={'User has Entra ID'} /> :
                                                        <ShieldIcon className="fa-light fa-id-card text-danger" data-tip={'User does not have Entra ID'} />
                                                }
                                            </div>
                                            <div>
                                                <Button
                                                    onClick={syncWithAdp}
                                                >
                                                    <Icon className={'fa-duotone fa-solid fa-rotate pr-2'} size={20} />
                                                    Sync with ADP
                                                </Button>
                                            </div>
                                        </FlexContainer>
                                    )
                                }
                            </span>
                            <i
                                className={'fal fa-question float-right'}
                                style={{ fontSize: '24px' }}
                                onClick={() =>
                                    setCustomWikiTitle('POPUP_EMPLOYEE_DETAILS')
                                }
                                data-tip={'Wiki'}
                            />
                        </Col>
                    </Row>
                </ModalHeader>

                <Formik
                    initialValues={initialValues}
                    validationSchema={EmployeeSchema}
                    validateOnMount={true}
                    enableReinitialize={true}
                    validateOnChange={true}
                >
                    {({
                        initialValues,
                        values,
                        isValid,
                        setFieldValue,
                        validateForm,
                        errors,
                        resetForm,
                    }) => (
                        <>
                            {/* {console.log(errors)} */}
                            <ModalBody>
                                <Nav tabs className="separator-tabs ml-0 mb-2">
                                    <NavItem>
                                        <NavLink
                                            location={{}}
                                            to="#"
                                            className={classnames({
                                                active: activeTab === 'INFO',
                                                'nav-link': true,
                                            })}
                                            onClick={() => {
                                                toggleTab('INFO');
                                            }}
                                        >
                                            Employee Info.
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink
                                            location={{}}
                                            to="#"
                                            className={classnames({
                                                active: activeTab === 'ADDITIONAL',
                                                'nav-link': true,
                                            })}
                                            onClick={() => {
                                                toggleTab('ADDITIONAL');
                                            }}
                                        >
                                            Additional Data
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink
                                            location={{}}
                                            to="#"
                                            className={classnames({
                                                active: activeTab === 'FILES',
                                                'nav-link': true,
                                            })}
                                            onClick={() => {
                                                toggleTab('FILES');
                                            }}
                                        >
                                            Files
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink
                                            location={{}}
                                            to="#"
                                            className={classnames({
                                                active: activeTab === 'LOG',
                                                'nav-link': true,
                                            })}
                                            onClick={() => {
                                                toggleTab('LOG');
                                            }}
                                        >
                                            Log
                                        </NavLink>
                                    </NavItem>
                                </Nav>
                                <TabContent
                                    activeTab={activeTab}
                                    className={'mt-2'}
                                >
                                    <TabPane tabId={'INFO'}>
                                        <EmployeeForm
                                            values={values}
                                            uniqueEmail={uniqueEmail}
                                            uniqueNumber={uniqueNumber}
                                            unitsMap={unitsMap}
                                            selectedUnits={selectedUnits}
                                            setFieldValue={setFieldValue}
                                            handleSelectUnit={handleSelectUnit}
                                            newEmployee={newEmployee}
                                            statusOptions={statusOptions}
                                            restrictedAccess={restrictedAccess}
                                            sortedAccessLevels={sortedAccessLevels}
                                            handleModalAirlines={handleModalAirlines}
                                            ipAddresses={ipAddresses}
                                            handleCreateUpdateIp={handleCreateUpdateIp}
                                            setManageAccessMap={setManageAccessMap}
                                            setManageNativeAccessMap={setManageNativeAccessMap}
                                            jobTitles={jobTitles}
                                        />
                                    </TabPane>
                                    <TabPane tabId={'ADDITIONAL'}>
                                        <AdditionalEmployeeData
                                            selectedEmployee={selectedEmployee}
                                            triggerAdpSync={triggerAdpSync}
                                            toggle={toggle}
                                        />
                                    </TabPane>
                                    <TabPane tabId={'FILES'}>
                                        <Files
                                            user={user}
                                            employeeFiles={employeeFiles}
                                            setEmployeeFiles={setEmployeeFiles}
                                            selectedEmployee={selectedEmployee}
                                        />
                                    </TabPane>
                                    <TabPane tabId={'LOG'}>
                                        <EmployeeLog
                                            selectedEmployee={selectedEmployee}
                                        />
                                    </TabPane>
                                </TabContent>
                            </ModalBody>
                            <ModalFooter style={{ width: '100%' }}>
                                <Row style={{ width: '100%' }}>
                                    <Col md={12}>
                                        {!newEmployee && (
                                            <div className={'float-left'}>
                                                {
                                                    restrictedAccess && (
                                                        <ActionIcon
                                                            type={'delete'}
                                                            onClick={() => deleteEmployee(selectedEmployee.s_email)}
                                                            className={'pr-2'}
                                                        />
                                                    )
                                                }
                                                Created by{' '}
                                                {selectedEmployee.s_created_by}{' '}
                                                at{' '}
                                                {moment
                                                    .utc(
                                                        selectedEmployee.t_created
                                                    )
                                                    .format(
                                                        'MM/DD/YYYY HH:mm:ss'
                                                    )}
                                                . Updated by{' '}
                                                {selectedEmployee.s_modified_by}{' '}
                                                at{' '}
                                                {moment
                                                    .utc(
                                                        selectedEmployee.t_modified
                                                    )
                                                    .format(
                                                        'MM/DD/YYYY HH:mm:ss'
                                                    )}
                                            </div>
                                        )}
                                        {
                                            activeTab === 'INFO' && (
                                                <div className={'float-right'}>
                                                    <SaveButton
                                                        enableSave={
                                                            Object.keys(errors).length <
                                                            1 &&
                                                            uniqueEmail(values.s_email) &&
                                                            uniqueNumber(values.i_employee_number) &&
                                                            values.s_email
                                                        }
                                                        handleSave={() =>
                                                            handleAddEditEmployee(
                                                                initialValues,
                                                                values,
                                                                selectedAirlines,
                                                                resetForm
                                                            )
                                                        }
                                                        className={''}
                                                    />
                                                </div>
                                            )
                                        }
                                    </Col>
                                </Row>
                            </ModalFooter>
                        </>
                    )}
                </Formik>

                <ModalManageAirlines
                    modal={modalAirlines}
                    setModal={setModalAirlines}
                    airlines={airlines}
                    selectedAirlines={selectedAirlines}
                    addRemoveAirline={addRemoveAirline}
                    selectedEmployee={selectedEmployee}
                />

                <ModalAccessMap
                    user={user}
                    modal={manageAccessMap}
                    setModal={setManageAccessMap}
                    selectedEmployee={selectedEmployee}
                />

                <ModalNativeAccessMap
                    user={user}
                    modal={manageNativeAccessMap}
                    setModal={setManageNativeAccessMap}
                    selectedEmployee={selectedEmployee}
                    nativeAccessMapSchema={nativeAccessMapSchema}
                />

                <ModalUpdatePhoto
                    modal={modalUpdatePhoto}
                    setModal={setModalUpdatePhoto}
                    selectedEmployee={selectedEmployee}
                    setSelectedEmployee={setSelectedEmployee}
                    user={user}
                />
            </Modal>
        </div>
    );
}

const FlexContainer = styled.div`
    display: flex;
    gap: 25px;
    margin-top: 25px;
`;

const ImgUser = styled.img`
    border-radius: 50%;
    width: 100px;
    height: 100px;
`;

const EditIcon = styled.i`

`;

const ShieldIcon = styled.i`
    font-size: 26px;
`;